<template>
  <!-- Base -->
  <CommonsHeader :data="baseData?.navigation" :siteSetting="siteSettingsData" />
  <main :class="{ 'mt-[71px] lg:mt-[90px]': !isHomePage }">
    <slot />
  </main>
  <CommonsFooter :data="baseData?.footer" :siteSetting="siteSettingsData" />
  <!-- Widgets -->
  <client-only>
    <WidgetsCookie />
  </client-only>

  <Transition name="widget-fade" mode="out-in">
    <WidgetsStickyQuote
      v-if="siteSettingsData?.startProject"
      v-show="stickyQuoteVisible"
    />
  </Transition>
  <!-- Modals -->
  <PopupModal
    v-if="popupModalMounted"
    :visible="modalVisible"
    :data="siteSettingsData?.popup"
    @update:visible="modalVisible = $event"
  />
  <!-- Toast -->
  <client-only>
    <Toast position="bottom-center" />
  </client-only>

  <!-- WhatsApp -->
  <div
    v-if="shouldShowFloatingWidgets"
    class="flex flex-col gap-2 fixed bottom-2.5 right-2.5 lg:bottom-4 lg:right-4 z-20"
  >
    <a
      v-if="siteSettingsData?.fixedMenu?.instagramButton"
      :href="siteSettingsData?.fixedMenu?.instagramLink"
      target="_blank"
      class="w-12 h-12 lg:w-14 lg:h-14 flex items-center justify-center rounded-full bg-[linear-gradient(45deg,#F58529,#DD2A7B,#8134AF,#515BD4)] shadow-lg transition-all duration-300 ease-out hover:scale-110 hover:brightness-110"
      aria-label="instagram"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 448 512"
        width="28"
        height="28"
        fill="white"
      >
        <path
          d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9 0 63.6 51.3 114.9 114.9 114.9 63.6 0 114.9-51.3 114.9-114.9 0-63.6-51.3-114.9-114.9-114.9zm0 189.6 c-41.1 0-74.7-33.5-74.7-74.7 0-41.1 33.5-74.7 74.7-74.7 41.1 0 74.7 33.5 74.7 74.7 0 41.1-33.6 74.7-74.7 74.7zm146.4-194.3 c0 14.9-12 26.8-26.8 26.8 -14.9 0-26.8-12-26.8-26.8 s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2 c-1.7-35.7-9.9-67.2-36.2-93.5 -26.2-26.2-57.7-34.4-93.5-36.2 -37-2.1-147.9-2.1-184.9 0 -35.7 1.7-67.2 9.9-93.5 36.2 -26.2 26.2-34.4 57.7-36.2 93.5 -2.1 37-2.1 147.9 0 184.9 1.7 35.7 9.9 67.2 36.2 93.5 26.2 26.2 57.7 34.4 93.5 36.2 37 2.1 147.9 2.1 184.9 0 35.7-1.7 67.2-9.9 93.5-36.2 26.2-26.2 34.4-57.7 36.2-93.5 2.1-37 2.1-147.9 0-184.9zm-48.5 224.7 c-7.8 19.6-22.9 34.7-42.5 42.5 -29.4 11.7-99.2 9-131.5 9 s-102.1 2.6-131.5-9 c-19.6-7.8-34.7-22.9-42.5-42.5 -11.7-29.4-9-99.2-9-131.5 s-2.6-102.1 9-131.5 c7.8-19.6 22.9-34.7 42.5-42.5 29.4-11.7 99.2-9 131.5-9 s102.1-2.6 131.5 9 c19.6 7.8 34.7 22.9 42.5 42.5 11.7 29.4 9 99.2 9 131.5 s2.7 102.1-9 131.5z"
        />
      </svg>
    </a>
    <a
      v-if="siteSettingsData?.fixedMenu?.whatsappButton"
      :href="`https://api.whatsapp.com/send?phone=${siteSettingsData?.fixedMenu?.whatsappPhone.replace(/\s+/g, '')}&amp;text=${siteSettingsData?.fixedMenu?.whatsappText}`"
      target="_blank"
      class="w-12 h-12 lg:w-14 lg:h-14 flex items-center justify-center rounded-full bg-[#25d366] shadow-lg transition-all duration-300 ease-out hover:scale-110 hover:brightness-110"
      aria-label="WhatsApp ile yaz"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 448 512"
        width="28"
        height="28"
        fill="white"
      >
        <path
          d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7 .9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"
        />
      </svg>
    </a>
    <a
      v-if="siteSettingsData?.fixedMenu?.phoneButton"
      :href="telHref(siteSettingsData?.fixedMenu?.phoneNumber)"
      target="_blank"
      class="w-12 h-12 lg:w-14 lg:h-14 flex items-center justify-center rounded-full bg-brand-second shadow-lg transition-all duration-300 ease-out hover:scale-110 hover:brightness-110"
      aria-label="Telefon ile ara"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 512 512"
        width="24"
        height="24"
        fill="white"
      >
        <path
          d="M164.9 24.6c-7.7-18.6-28-28.5-47.4-23.2l-88 24C12.1 30.2 0 46 0 64C0 311.4 200.6 512 448 512c18 0 33.8-12.1 38.6-29.5l-24-88c5.3-19.4-4.6-39.7-23.2-47.4l-96-40c-16.3-6.8-35.2-2.1-46.3 11.6L304.7 368C234.3 334.7 177.3 277.7 144 207.3L193.3 167c13.7-11.2 18.4-30 11.6-46.3l-40-96z"
        />
      </svg>
    </a>
  </div>
  <!-- WhatsApp -->
</template>
<script setup>
import { defineAsyncComponent, nextTick, onMounted, onBeforeUnmount, ref, computed, watch, watchEffect } from "vue";
import { useApi } from "~/server/getApi";
import { useLanguagesStore, useSiteSettingsStore } from "@/store/index";
import { useI18n } from "vue-i18n";
import { useIsMobile } from "~/composables/utils";
import {
  createLocalBusinessSchema,
  buildJsonLdScripts,
} from "~/composables/schemaOrg";

const { locale } = useI18n();
const route = useRoute();
const useSiteSettings = useSiteSettingsStore();
const useLanguages = useLanguagesStore();
const modalVisible = ref(false);
const PopupModal = defineAsyncComponent(
  () => import("~/components/Plugins/Modal/Modal.vue"),
);
const runtimeConfig = useRuntimeConfig();
const isAtPageBottom = ref(false);
const { isMobile } = useIsMobile();
const shouldShowFloatingWidgets = computed(
  () => !isMobile.value || !isAtPageBottom.value,
);
const canShowStickyQuote = ref(false);
const stickyQuoteVisible = computed(
  () => shouldShowFloatingWidgets.value && canShowStickyQuote.value,
);
const scrollIdleDelay = 500;
let scrollIdleTimeoutId;

const updateIsAtBottom = () => {
  if (typeof document === "undefined") return;
  const body = document.documentElement;
  isAtPageBottom.value =
    window.innerHeight + window.scrollY >= body.scrollHeight - 100;
};

const setScrollIdleTimer = () => {
  if (scrollIdleTimeoutId) {
    clearTimeout(scrollIdleTimeoutId);
  }
  scrollIdleTimeoutId = window.setTimeout(() => {
    canShowStickyQuote.value = true;
  }, scrollIdleDelay);
};

let scrollTicking = false;
const handleScroll = () => {
  canShowStickyQuote.value = false;
  if (!scrollTicking) {
    scrollTicking = true;
    requestAnimationFrame(() => {
      updateIsAtBottom();
      scrollTicking = false;
    });
  }
  setScrollIdleTimer();
};

const {
  data: baseData,
  fetchData: fetchbaseData,
} = useApi(`base?pLevel`);
const {
  data: siteSettingsData,
  fetchData: fetchSiteSettingsData,
} = useApi(`site-setting?pLevel`);
const { data: contactPageData, fetchData: fetchContactPageData } =
  useApi(`contact?&pLevel`);
const {
  data: localesData,
  fetchData: fetchlocalesData,
} = useApi("i18n/locales?pLevel");

const popupEnabled = computed(() => !!siteSettingsData.value?.popup?.enabled);
const shouldMountPopupModal = computed(
  () => popupEnabled.value || modalVisible.value,
);
const { mounted: popupModalMounted } = useLazyMount(shouldMountPopupModal, 250);
const isHomePage = computed(() => {
  const name = route.name?.toString() || "";
  return (
    route.path === "/" ||
    route.path === "/en" ||
    route.path === "/ar" ||
    name.startsWith("index")
  );
});

const resolveAssetUrl = (path) => {
  if (!path) return null;
  return path.startsWith("http")
    ? path
    : `${runtimeConfig.public.baseUrl}${path}`;
};

const applyDynamicFont = (settings) => {
  const fontNameRaw =
    settings?.fontName ||
    settings?.attributes?.fontName ||
    settings?.data?.attributes?.fontName;

  const sanitizedFontName = fontNameRaw
    ?.replace(/[^A-Za-z0-9\s-]/g, " ")
    ?.trim();

  if (!sanitizedFontName) {
    useSiteSettings.setFontFamily("");
    return;
  }

  useSiteSettings.setFontFamily(sanitizedFontName);

  const fontQuery = sanitizedFontName.replace(/\s+/g, "+");
  const weights = "300;400;500;600;700";

  useHead({
    link: [
      {
        key: "site-google-font",
        rel: "stylesheet",
        href: `https://fonts.googleapis.com/css2?family=${fontQuery}:wght@${weights}&display=swap`,
      },
    ],
  });
};

const applyDynamicColors = (settings) => {
  useSiteSettings.setColors(settings);
};

// Centralized brand color injection for Nuxt 4 SSR reliability
useHead(() => {
  const vars = useSiteSettings.brandColorVariables;
  if (!vars || !vars.length) return { style: [] };
  const styleContent = vars
    .map(([name, value]) => `${name}: ${value};`)
    .join("");
  return {
    style: [
      {
        key: "site-brand-colors",
        innerHTML: `:root{${styleContent}}`,
      },
    ],
  };
});

const syncSiteSettings = (settingsRaw) => {
  if (!settingsRaw) return;
  
  // Handle various potential wrapper structures for Strapi single types
  const settings = settingsRaw?.attributes || settingsRaw?.data?.attributes || settingsRaw?.data || settingsRaw;

  useSiteSettings.setBrandName(
    settings.brandName ||
      settings.companyName ||
      settings.siteTitle ||
      settings.siteName ||
      settings.title ||
      ""
  );

  const phoneValue = settings.phone || settings.phoneNumber || settings.mobile || settings.gsm || "";
  useSiteSettings.setPhone(phoneValue ? String(phoneValue).replace(/\s+/g, "") : "");

  useSiteSettings.setPolicy(settings.policy || settings.kvkk || "");
  useSiteSettings.setformType(settings.formType || "offer");

  const instagramValue = settings.instagramToken || settings.instagram_token || settings.instagram || "";
  useSiteSettings.setInstagramToken(instagramValue || "");

  useSiteSettings.setRecaptchaToken(settings.recaptchaToken || settings.recaptcha_token || "");
  useSiteSettings.setFormServices(settings.formServices || settings.services || []);

  const logoUrl = settings.headerLogo?.url || settings.logo?.url || settings.logo || "";
  useSiteSettings.setLogo(logoUrl);

  const heroShape = settings.heroShape !== undefined ? settings.heroShape : settings.hero_shape;
  const footerShape = settings.footerShape !== undefined ? settings.footerShape : settings.footer_shape;
  useSiteSettings.setShape(!!heroShape, !!footerShape);
  
  useSiteSettings.setSocialMedia(settings?.socialMedia || settings?.social_media || []);
  
  useSiteSettings.setFixedMenu(settings?.fixedMenu || settings?.fixed_menu || null);
  
  const animateScroll = settings.animateScroll !== undefined ? settings.animateScroll : settings.animate_scroll;
  useSiteSettings.setAnimateScroll(animateScroll !== false);

  const roundCorner = settings.roundCorner !== undefined ? settings.roundCorner : settings.round_corner;
  const isRound = roundCorner === true || roundCorner === 'true';
  useSiteSettings.setRoundCorner(isRound);
  
  useHead({
    htmlAttrs: {
      class: isRound ? "roundedCorner" : "",
    },
  });

  applyDynamicColors(settings);
  applyDynamicFont(settings);
};

// Initial data fetching during setup/SSR
await Promise.all([
  fetchSiteSettingsData(),
  fetchbaseData(),
  fetchlocalesData()
]);

if (localesData.value) {
  useLanguages.setLanguages(localesData.value);
}

// Initial sync
if (siteSettingsData.value) {
  syncSiteSettings(siteSettingsData.value);
}

// Fetch footer contact separately as it depends on locale and might not be as critical for immediate hero render
const ensureFooterContactLoaded = async () => {
  const hasCachedLocaleData =
    useSiteSettings.footerContact &&
    useSiteSettings.footerContactLocale === locale.value;
  if (hasCachedLocaleData) return;
  
  await fetchContactPageData();
  if (contactPageData.value) {
    useSiteSettings.setFooterContact(contactPageData.value, locale.value);
  }
};

await ensureFooterContactLoaded();

useHead(() => {
  const contact = useSiteSettings.footerContact;
  const brandName = useSiteSettings.brandName;
  const logo = useSiteSettings.logo;
  const siteUrl = runtimeConfig.public.siteUrl;
  const baseUrl = runtimeConfig.public.baseUrl;

  if (!brandName || !siteUrl) return { script: [] };

  const scripts = buildJsonLdScripts([
    {
      key: "schema-global-business",
      schema: createLocalBusinessSchema({
        name: brandName,
        url: siteUrl,
        logoUrl: logo ? (logo.startsWith("http") ? logo : `${baseUrl}${logo}`) : undefined,
        telephone: contact?.phone,
        address: contact?.address
          ? {
              streetAddress: contact.address,
            }
          : undefined,
        sameAs: useSiteSettings.socialMedia?.map((s) => s.link).filter(Boolean),
      }),
    },
  ]);

  return { script: scripts };
});

watchEffect(() => {
  const faviconPath = siteSettingsData.value?.faviconLogo?.url;
  const faviconHref = resolveAssetUrl(faviconPath) || "/favicon.ico";

  useHead({
    link: [
      {
        key: "app-favicon",
        rel: "icon",
        type: "image/png",
        href: faviconHref,
      },
    ],
  });
});


// Scripts & Tracking Integration
useHead(() => {
  const data = siteSettingsData.value?.attributes || siteSettingsData.value?.data?.attributes || siteSettingsData.value?.data || siteSettingsData.value;
  if (!data) return { script: [], noscript: [] };

  // Tracking fields are nested inside the "scripts" component in Strapi
  const s = data.scripts || {};

  const headScripts = [];
  const noscripts = [];

  // 1. Google Analytics
  if (s.googleAnalyticsId) {
    headScripts.push(
      {
        src: `https://www.googletagmanager.com/gtag/js?id=${s.googleAnalyticsId}`,
        async: true,
        key: 'ga-js'
      },
      {
        innerHTML: `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${s.googleAnalyticsId}');
        `,
        type: "text/javascript",
        key: 'ga-config'
      }
    );
  }

  // 2. Tag Manager
  if (s.tagManagerId) {
    headScripts.push({
      innerHTML: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${s.tagManagerId}');`,
      type: "text/javascript",
      key: 'gtm-js'
    });
    noscripts.push({
      innerHTML: `<iframe src="https://www.googletagmanager.com/ns.html?id=${s.tagManagerId}"
      height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
      tagPosition: 'bodyOpen',
      key: 'gtm-noscript'
    });
  }

  // 3. Meta Pixel
  if (s.metaPixelId) {
    headScripts.push({
      innerHTML: `!function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${s.metaPixelId}');
      fbq('track', 'PageView');`,
      type: "text/javascript",
      key: 'meta-pixel-js'
    });
    noscripts.push({
      innerHTML: `<img height="1" width="1" style="display:none"
      src="https://www.facebook.com/tr?id=${s.metaPixelId}&ev=PageView&noscript=1" />`,
      tagPosition: 'bodyOpen',
      key: 'meta-pixel-noscript'
    });
  }

  // 4. Custom Scripts (Header, Body Start, Body End, Tracking)
  if (s.headerScripts) {
    headScripts.push({ innerHTML: s.headerScripts, type: 'text/javascript', key: 'header-custom' });
  }
  if (s.bodyStartScripts) {
    headScripts.push({ innerHTML: s.bodyStartScripts, type: 'text/javascript', tagPosition: 'bodyOpen', key: 'body-start-custom' });
  }
  if (s.bodyEndScripts) {
    headScripts.push({ innerHTML: s.bodyEndScripts, type: 'text/javascript', tagPosition: 'bodyClose', key: 'body-end-custom' });
  }
  if (s.customTrackingScripts) {
    headScripts.push({ innerHTML: s.customTrackingScripts, type: 'text/javascript', key: 'tracking-custom' });
  }

  return { script: headScripts, noscript: noscripts };
});

onMounted(() => {
  updateIsAtBottom();
  setScrollIdleTimer();
  window.addEventListener("scroll", handleScroll, { passive: true });

  const data = siteSettingsData.value?.attributes || siteSettingsData.value?.data?.attributes || siteSettingsData.value?.data || siteSettingsData.value;
  if (data) {
    const popup = data.popup;
    if (popup?.enabled) {
      const now = new Date();
      const start = popup.startDate ? new Date(popup.startDate) : null;
      const end = popup.endDate ? new Date(popup.endDate) : null;

      const isWithinTimeRange =
        (!start && !end) ||
        (start && !end && now >= start) ||
        (start && end && now >= start && now <= end);
      if (isWithinTimeRange && !sessionStorage.getItem("mainModal")) {
        sessionStorage.setItem("mainModal", "true");
        nextTick(() => {
          modalVisible.value = true;
        });
      }
    }
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", handleScroll);
  if (scrollIdleTimeoutId) {
    clearTimeout(scrollIdleTimeoutId);
  }
});

watch(
  locale,
  async () => {
    await Promise.all([
      fetchbaseData(),
      ensureFooterContactLoaded(),
      fetchSiteSettingsData()
    ]);
  },
  { flush: "post" },
);

watch(
  siteSettingsData,
  (settings) => {
    if (settings) {
      syncSiteSettings(settings);
    }
  },
  { immediate: true }
);
</script>
